// pageInside/payments/PayPalRecipientCard.jsx
//
// Banking -> PayPal Account: the PayPal receiver of PayPal withdrawals.
// Only an email identifier is stored (never a PayPal password); only the
// masked form is ever shown. Elakay never claims "Verified" on save: the
// receiver is confirmed only when a PayPal payout succeeds.
import React, { useEffect, useState } from "react";
import { FaPaypal } from "react-icons/fa";
import { elakayAutoText } from "../dataElakay";
import { getPayPalRecipientAPI, setPayPalRecipientAPI, removePayPalRecipientAPI } from "../../auth/paymentsApi";
import { paymentErrorMessage } from "./walletLabels";

const t = elakayAutoText;

const statusLabel = (status) => ({
  unverified: t("h_pay_pp_unverified", "Not confirmed yet — confirmed by PayPal on your first successful withdrawal"),
  receiving_confirmed: t("h_pay_pp_confirmed", "Confirmed by PayPal"),
  rejected: t("h_pay_pp_rejected", "Refused by PayPal — update this account"),
}[status] || "");

// enabled     = the Family allows PayPal withdrawals (the account can be saved);
// operational = PayPal Payouts is really usable right now.
const PayPalRecipientCard = ({ enabled = true, operational = true, onChange }) => {
  const [state, setState] = useState({ loading: true, recipient: null });
  const [editing, setEditing] = useState(false);
  const [email, setEmail] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => getPayPalRecipientAPI()
    .then((res) => setState({ loading: false, recipient: res.recipient }))
    .catch((e) => { setState({ loading: false, recipient: null }); setError(paymentErrorMessage(e)); });
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const res = await setPayPalRecipientAPI({ email, isDefaultWithdrawal: isDefault, password });
      setState({ loading: false, recipient: res.recipient });
      setEditing(false); setEmail(""); setPassword("");
      onChange && onChange(res.recipient);
    } catch (err) {
      setError(paymentErrorMessage(err));
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(t("h_pay_pp_remove_confirm", "Remove this PayPal account from Elakay?"))) return;
    const confirmPassword = window.prompt(t("h_pay_confirm_account_password", "Confirm your password"));
    if (!confirmPassword) return;
    setBusy(true); setError("");
    try {
      const res = await removePayPalRecipientAPI(confirmPassword);
      setState({ loading: false, recipient: res.recipient });
      onChange && onChange(res.recipient);
    } catch (err) {
      setError(paymentErrorMessage(err));
    } finally { setBusy(false); }
  };

  if (!enabled && !state.recipient?.connected) return null;
  const r = state.recipient;
  return (
    <div className="elk-ps__group" style={{ marginTop: 12 }}>
      <h3 style={{ display: "flex", alignItems: "center", gap: 8 }}><FaPaypal aria-hidden="true" />{t("h_pay_paypal_account", "PayPal Account")}</h3>
      {!enabled && <div className="elk-notice elk-notice--warn">{t("h_pay_withdrawal_method_unavailable", "Withdrawal Method Unavailable")}</div>}
      {enabled && !operational && <div className="elk-notice elk-notice--warn">{t("h_pay_pp_not_operational", "PayPal Payouts is not available yet. You can save your PayPal account now; withdrawals to PayPal open as soon as Elakay activates them.")}</div>}
      {state.loading ? <div className="elk-pay-hint">{t("h_pay_loading", "Loading...")}</div> : (
        <>
          {r?.connected && !editing && (
            <div>
              <div className="elk-summary-row"><span>{t("h_pay_paypal_recipient", "PayPal recipient")}</span><strong>{r.maskedEmail}</strong></div>
              <div className="elk-pay-hint">{statusLabel(r.status)}{r.isDefaultWithdrawal ? ` · ${t("h_pay_default_destination", "Default withdrawal destination")}` : ""}</div>
              <div className="elk-wallet__actions">
                <button type="button" className="elk-btn elk-btn--ghost" disabled={busy || !enabled} onClick={() => { setEditing(true); setIsDefault(Boolean(r.isDefaultWithdrawal)); }}>{t("h_pay_edit", "Edit")}</button>
                <button type="button" className="elk-btn elk-btn--danger" disabled={busy} onClick={remove}>{t("h_pay_remove", "Remove")}</button>
              </div>
            </div>
          )}
          {enabled && (!r?.connected || editing) && (
            <form onSubmit={save}>
              <div className="elk-field">
                <label htmlFor="elk-pp-email">{t("h_pay_paypal_email", "PayPal account email")}</label>
                <input id="elk-pp-email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
                <span className="elk-pay-hint">{t("h_pay_pp_never_password", "Elakay never asks for your PayPal password.")}</span>
              </div>
              <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14, marginBottom: 12 }}>
                <span className="elk-switch"><input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} /><span className="elk-switch__track" aria-hidden="true" /></span>
                {t("h_pay_default_destination", "Default withdrawal destination")}
              </label>
              <div className="elk-field">
                <label htmlFor="elk-pp-password">{t("h_pay_confirm_account_password", "Confirm your password")}</label>
                <input id="elk-pp-password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
              </div>
              <div className="elk-wallet__actions">
                <button type="submit" className="elk-btn elk-btn--primary" disabled={busy}>{t("h_pay_save", "Save")}</button>
                {editing && <button type="button" className="elk-btn elk-btn--ghost" onClick={() => setEditing(false)}>{t("h_pay_cancel", "Cancel")}</button>}
              </div>
            </form>
          )}
        </>
      )}
      {error && <div className="elk-notice elk-notice--error" role="alert">{error}</div>}
    </div>
  );
};

export default PayPalRecipientCard;
